import type { PolicyFitStatus } from '@ballerine/common';
import { BookText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PolicyFitIcon } from '../../report-shared/components/PolicyFitBadge/PolicyFitBadge';
import { ReportWrapper } from '../../report-shared/components/ReportWrapper';
import React from 'react';

type YourPolicyFitSectionProps = {
  policyFit?: {
    status?: PolicyFitStatus;
    description?: string | null;
    displayName?: string | null;
    version?: number | null;
  } | null;
  customer?: {
    displayName?: string | null;
  } | null;
};

const markdownComponents: Components = {
  p: ({ node, ...props }) => (
    <p className="leading-relaxed mb-2" style={{ fontSize: '14px' }} {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal list-inside space-y-1" style={{ fontSize: '14px' }} {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="list-disc list-inside space-y-1" style={{ fontSize: '14px' }} {...props} />
  ),
  li: ({ node, ...props }) => <li style={{ fontSize: '14px' }} {...props} />,
  a: ({ node, ...props }) => (
    <a
      className="text-blue-600 text-[0.7rem] hover:text-blue-800 hover:underline font-medium underline-offset-2 underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  table: ({ node, ...props }) => (
    <table className="my-4 w-full border-collapse text-sm" {...props} />
  ),
  thead: ({ node, ...props }) => <thead className="bg-slate-50" {...props} />,
  tbody: ({ node, ...props }) => <tbody {...props} />,
  tr: ({ node, ...props }) => <tr className="border-b last:border-b-0" {...props} />,
  th: ({ node, ...props }) => (
    <th className="border border-slate-200 px-3 py-2 text-left font-semibold" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="border border-slate-200 px-3 py-2 align-top" {...props} />
  ),
};

export const YourPolicyFitSection: React.FC<YourPolicyFitSectionProps> = ({
  policyFit,
  customer,
}) => {
  if (!policyFit) {
    return null;
  }

  return (
    <ReportWrapper showTooltip={false}>
      <div className="flex flex-col gap-4 pt-10">
        <div className="flex justify-between items-start">
          <h2 className="text-xl font-semibold text-slate-800">Your Policy Fit</h2>
          <div className="flex items-center gap-4">
            {customer?.displayName && policyFit.version != null && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <BookText className="w-4 h-4" />
                Based on {customer?.displayName} v.{policyFit.version}
              </span>
            )}
            <PolicyFitIcon policyFit={policyFit} variant="simple" withStatusText={true} />
          </div>
        </div>

        {policyFit.description && (
          <div className="text-sm leading-snug prose prose-sm max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {policyFit.description}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </ReportWrapper>
  );
};
